import { useEffect, useState } from 'react';
import type { AccountDto, AccountLedgerResponse } from '@hesabat/contracts';
import { api, ProblemError, UnauthorizedError } from './api';
import { downloadCsv } from './csv';
import ReportActions from './ReportActions';
import { formatMoney, isZero } from './money';
import Loading from './Loading';
import Hint from './Hint';

interface Props {
  companyId: string;
  currency: string;
  accounts: readonly AccountDto[];
  accountCode: string | null;
  onChangeAccount: (accountCode: string) => void;
  onSessionLost: () => void;
}

export const SOURCE_LABELS: Record<string, string> = {
  manual_journal: 'قيد يدوى',
  opening_balance: 'رصيد افتتاحى',
  sales_invoice: 'فاتورة مبيعات',
  sales_credit_note: 'إشعار دائن',
  sales_debit_note: 'إشعار مدين',
  customer_receipt: 'تحصيل',
  customer_advance: 'دفعة مقدمة من عميل',
  advance_recognition: 'إثبات دفعة مقدمة',
  receipt_application: 'تسوية دفعة على فاتورة',
  purchase_invoice: 'فاتورة مشتريات',
  purchase_debit_note: 'إشعار مدين لمورد',
  purchase_credit_note: 'إشعار دائن من مورد',
  supplier_payment: 'سداد مورد',
  expense: 'مصروف',
  treasury_transfer: 'تحويل خزينة',
  prepaid_payment: 'مصروف مقدم',
  prepaid_amortization: 'استهلاك مقدم',
  fixed_asset_purchase: 'شراء أصل',
  depreciation: 'إهلاك',
  stock_receipt: 'استلام مخزون',
  stock_issue: 'صرف مخزون',
  stock_opening: 'رصيد مخزون افتتاحى',
  stock_adjustment: 'جرد وتسوية مخزون',
  period_close: 'إقفال فترة',
  reversal: 'قيد عكسى',
  data_migration: 'ترحيل من البرنامج القديم',
  sales_invoice_cancellation: 'إلغاء فاتورة',
};

const NATURE_LABELS = { debit: 'مدين', credit: 'دائن', mixed: 'مختلط' } as const;

function yearBounds(): { from: string; to: string } {
  const year = new Date().getFullYear();
  return { from: year + '-01-01', to: year + '-12-31' };
}

/**
 * كشف حساب: افتتاحى، ثم كل سطر منشور برصيده الجارى، ثم ختامى.
 * الأرقام من الـ API كما هى (نصوص عشرية) — الواجهة تعرض ولا تعيد الحساب،
 * فلا يختلف ما يراه المستخدم عن الميزان أبدًا.
 */
export default function AccountLedger({
  companyId,
  currency,
  accounts,
  accountCode,
  onChangeAccount,
  onSessionLost,
}: Props) {
  const [period, setPeriod] = useState(yearBounds);
  const [ledger, setLedger] = useState<AccountLedgerResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!accountCode) {
      setLedger(null);
      return;
    }
    if (period.from > period.to) {
      setLedger(null);
      setError('تاريخ البداية بعد تاريخ النهاية.');
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    api
      .accountLedger(companyId, accountCode, period.from, period.to)
      .then((result) => {
        if (!cancelled) setLedger(result);
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        if (caught instanceof UnauthorizedError) {
          onSessionLost();
          return;
        }
        setLedger(null);
        if (caught instanceof ProblemError && caught.problem.status === 404) {
          setError('الحساب غير موجود فى دليل هذه الشركة.');
        } else if (caught instanceof ProblemError) {
          setError(caught.problem.detail ?? caught.problem.title);
        } else {
          setError(caught instanceof Error ? caught.message : String(caught));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [companyId, accountCode, period, onSessionLost]);

  const postable = accounts.filter((account) => account.isPostable);

  return (
    <div className="panel">
      <div className="panel-head">
        <h2>كشف حساب</h2>
        {ledger && (
          <ReportActions
            onExport={() =>
              downloadCsv(
                'كشف-' + ledger.account.code + '-' + ledger.from + '-' + ledger.to + '.csv',
                ['التاريخ', 'القيد', 'النوع', 'المرجع', 'البيان', 'مدين', 'دائن', 'الرصيد'],
                [
                  [ledger.from, '', '', '', 'رصيد افتتاحى', '', '', ledger.openingBalance],
                  ...ledger.lines.map((line) => [
                    line.postingDate,
                    line.journalNumber ?? line.journalId,
                    SOURCE_LABELS[line.sourceType] ?? line.sourceType,
                    line.reference,
                    line.memo,
                    line.debit,
                    line.credit,
                    line.runningBalance,
                  ]),
                  [
                    ledger.to,
                    '',
                    '',
                    '',
                    'الإجمالى والرصيد الختامى',
                    ledger.totalDebit,
                    ledger.totalCredit,
                    ledger.closingBalance,
                  ],
                ],
              )
            }
          />
        )}
      </div>

      <div className="form-row">
        <label className="grow">
          الحساب
          <select
            value={accountCode ?? ''}
            onChange={(event) => onChangeAccount(event.target.value)}
          >
            <option value="">— اختر حسابًا —</option>
            {postable.map((account) => (
              <option key={account.code} value={account.code}>
                {account.code} — {account.nameAr}
              </option>
            ))}
          </select>
          <Hint>اختر حسابًا لترى كل مبلغ دخله أو خرج منه، بالتاريخ والرصيد بعد كل حركة.</Hint>
        </label>
        <label>
          من
          <input
            type="date"
            value={period.from}
            onChange={(event) => setPeriod((current) => ({ ...current, from: event.target.value }))}
          />
        </label>
        <label>
          إلى
          <input
            type="date"
            value={period.to}
            onChange={(event) => setPeriod((current) => ({ ...current, to: event.target.value }))}
          />
        </label>
      </div>

      {!accountCode && <p className="sub">اختر حسابًا لعرض حركته.</p>}
      {loading && <Loading />}
      {error && <p className="error">{error}</p>}

      {ledger && !loading && (
        <>
          <p className="sub">
            {ledger.account.code} — {ledger.account.nameAr} • طبيعته{' '}
            {NATURE_LABELS[ledger.account.normalBalance]}. الرصيد الموجب فى اتجاه طبيعته، والسالب
            عكسها. المبالغ بـ {currency}.
          </p>
          <div className="table-scroll">
            <table className="stack-mobile">
              <thead>
                <tr>
                  <th>التاريخ</th>
                  <th>القيد</th>
                  <th>النوع</th>
                  <th>المرجع</th>
                  <th>البيان</th>
                  <th>مدين</th>
                  <th>دائن</th>
                  <th>الرصيد</th>
                </tr>
              </thead>
              <tbody>
                <tr className="opening">
                  <td>{ledger.from}</td>
                  <td colSpan={6}>رصيد افتتاحى</td>
                  <td data-label="الرصيد" className="num-cell">
                    {formatMoney(ledger.openingBalance)}
                  </td>
                </tr>
                {ledger.lines.map((line) => (
                  <tr key={line.journalId + ':' + line.lineNumber}>
                    <td data-label="التاريخ">{line.postingDate}</td>
                    <td data-label="القيد" className="mono" title={line.journalId}>
                      {line.journalNumber ?? line.journalId.slice(0, 8)}
                    </td>
                    <td data-label="النوع">{SOURCE_LABELS[line.sourceType] ?? line.sourceType}</td>
                    <td data-label="المرجع">{line.reference ?? ''}</td>
                    <td data-label="البيان">{line.memo ?? ''}</td>
                    <td data-label="مدين" className="num-cell">
                      {isZero(line.debit) ? '' : formatMoney(line.debit)}
                    </td>
                    <td data-label="دائن" className="num-cell">
                      {isZero(line.credit) ? '' : formatMoney(line.credit)}
                    </td>
                    <td data-label="الرصيد" className="num-cell">
                      {formatMoney(line.runningBalance)}
                    </td>
                  </tr>
                ))}
                {ledger.lines.length === 0 && (
                  <tr>
                    <td colSpan={8} className="sub">
                      لا حركة على الحساب فى هذه الفترة.
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr className="closing">
                  <td>{ledger.to}</td>
                  <td colSpan={4}>إجمالى الحركة والرصيد الختامى</td>
                  <td data-label="مدين" className="num-cell total">
                    {formatMoney(ledger.totalDebit)}
                  </td>
                  <td data-label="دائن" className="num-cell total">
                    {formatMoney(ledger.totalCredit)}
                  </td>
                  <td data-label="الرصيد" className="num-cell total">
                    {formatMoney(ledger.closingBalance)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
